import React, { useCallback } from 'react'

import styled from 'styled-components'
import themeGet from '@styled-system/theme-get'

import { useMutation } from '@apollo/client'
import { IconX } from '@tabler/icons-react'
import deleteCommunityUserProfileEntryMutation from 'Features/ProfilePanel/Mutations/deleteCommunityUserProfileEntry.graphql'

import uniqBy from 'lodash/uniqBy'

import { Dialog } from 'Components/Blocks/Modals'
import { resetButton } from 'Components/Styles'
import { Column, Row } from 'Components/UI'
import { Text } from 'Components/UI/_v2'

import { useCommunity, useEntityModal } from 'Hooks'

import EventBus from 'Services/EventBus'
import _, { useScopedI18n } from 'Services/I18n'
import toast from 'Services/Toast'

import colors from 'Theme/_v2/colors'

import SectionTitle from './SectionTitle/SectionTitle'

import ShowMore from '../Buttons/ShowMore'
import Card from '../Card'
import { IconContacts } from '../icons.styles'
import InlineTextboxCreate from '../InlineTextboxCreate/InlineTextboxCreate'

export const Container = styled.div`
  display: flex;
  gap: ${themeGet('space.2')}px;
`

export const CloseIcon = styled(IconX)`
  width: 12px;
  height: 12px;
  align-self: center;
  color: ${colors.icon.profile};
`

export const Button = styled.button`
  ${resetButton};

  display: flex;
  cursor: pointer;
`

export interface IFullName {
  id: string
  value?: string
}

export interface INamesProps {
  showCreate?: boolean
  communityUser: MainSchema.CommunityUser
}

function Names({ showCreate = true, communityUser }: INamesProps) {
  const { community } = useCommunity()
  const communityUserId = React.useMemo(
    () =>
      communityUser?.communityUsers?.find(e => e.communityId === community?.id)
        ?.communityUserId || null,
    [communityUser, community],
  )
  const [deleteModal, deleteActions] = useEntityModal<IFullName>()

  const s = useScopedI18n('blocks.profileEntry.name')

  const [proposedName, setProposedName] = React.useState<string>('')
  const [isAdding, setIsAdding] = React.useState(false)
  // const [isLoading, setLoading] = React.useState(false)

  const [deleteCommunityUserProfileEntry] = useMutation<
    Pick<MainSchema.Mutation, 'deleteCommunityUserProfileEntry'>,
    MainSchema.MutationDeleteCommunityUserProfileEntryArgs
  >(deleteCommunityUserProfileEntryMutation)

  // const [upsertFullNameCommunityUserProfileEntry] = useMutation<
  //   Pick<MainSchema.Mutation, 'upsertFullNameCommunityUserProfileEntry'>,
  //   MainSchema.MutationUpsertFullNameCommunityUserProfileEntryArgs
  // >(upsertFullNameCommunityUserProfileEntryMutation)

  // const handleOpenDeleteModal = deleteActions.openModal
  const handleDeleteModalClose = deleteActions.closeModal

  const fullNames: IFullName[] = React.useMemo(() => {
    if (!communityUser.fullNames) return []

    return uniqBy(
      communityUser.fullNames.map(record => ({
        id: record.profileEntry.id,
        value: record.profileEntry.value,
      })),
      'value',
    )
  }, [communityUser.fullNames])

  const { icon } = React.useMemo(
    () => ({
      icon: <IconContacts color={colors.icon.profile} height={12} width={12} />,
    }),
    [],
  )

  const handleDelete = useCallback(
    async (success: boolean) => {
      if (!communityUserId || !success || !deleteModal.entity?.id) return

      try {
        await deleteCommunityUserProfileEntry({
          variables: {
            input: {
              communityUserId,
              profileEntryId: deleteModal.entity.id,
            },
          },
        })

        // Reload the profile since there is no updater for this mutation
        EventBus.trigger(EventBus.actions.profile.reload)

        toast.success({
          title: 'Delete name',
          text: `Name successfully deleted`,
        })
      } catch (error) {
        let message = _('error.generic')

        if (error instanceof Error) {
          message = _(`error.${error.message || 'generic'}`)
        }

        toast.error({
          title: 'Delete name',
          text: message,
        })
      }
    },
    [deleteModal, deleteCommunityUserProfileEntry, communityUserId],
  )

  const handleChange = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { value } = event.target
      setProposedName(value)
    },
    [],
  )

  const handleCancel = React.useCallback(async () => {
    setIsAdding(false)
  }, [])

  // const handleSave = useCallback(async () => {
  //   if (!communityUserId || !proposedName.trim()) {
  //     return
  //   }

  //   setLoading(true)

  //   try {
  //     await upsertFullNameCommunityUserProfileEntry({
  //       variables: {
  //         input: {
  //           communityUserId,
  //           value: proposedName,
  //         },
  //       },
  //     })

  //     setProposedName('')
  //     setIsAdding(false)

  //     // TODO: Since there is no updater for this mutation, we need to reload the profile
  //     EventBus.trigger(EventBus.actions.profile.reload)

  //     toast.success({
  //       title: s('messageTitle'),
  //       text: s('success'),
  //     })
  //   } catch (error) {
  //     let message = _('error.generic')

  //     if (error instanceof Error) {
  //       message = _(`error.${error.message || 'generic'}`)
  //     }

  //     toast.error({
  //       title: s('messageTitle'),
  //       text: message,
  //     })
  //   } finally {
  //     setLoading(false)
  //   }
  // }, [
  //   communityUserId,
  //   upsertFullNameCommunityUserProfileEntry,
  //   proposedName,
  //   s,
  // ])

  const handleAddClick = React.useCallback(async () => {
    setIsAdding(true)
  }, [])

  if (fullNames.length <= 1) {
    return null
  }

  return (
    <>
      <Card>
        <Row>
          <Column>
            <SectionTitle
              icon={icon}
              showPlusButton={!!communityUserId && showCreate}
              title={s('title')}
              onPlusClicked={handleAddClick}
            />
          </Column>
          <Column fullWidth gap={1} pl={5}>
            {isAdding && (
              <Row fullWidth>
                <InlineTextboxCreate
                  disabled={!proposedName.trim()}
                  placeholder={s('placeholder')}
                  value={proposedName}
                  onCancel={handleCancel}
                  onChange={handleChange}
                  // onSave={handleSave}
                />
              </Row>
            )}
            {/* {isLoading && (
              <Row>
                <Loader />
              </Row>
            )} */}
            <ShowMore initialShown={3}>
              {fullNames.map(item => (
                <Container key={item.id}>
                  <Text
                    color={colors.text.primary}
                    ellipsis
                    fontSize={'14px'}
                    fontWeight={400}
                    lineHeight={'16px'}
                  >
                    {item.value}
                  </Text>
                  {/* <Button
                    onClick={event => {
                      event.preventDefault()
                      event.stopPropagation()

                      handleOpenDeleteModal({
                        id: item.id,
                        value: item.value,
                      })
                    }}
                  >
                    <CloseIcon />
                  </Button> */}
                </Container>
              ))}
            </ShowMore>
          </Column>
        </Row>
      </Card>

      {deleteModal.entity && (
        <Dialog
          content={`Are you sure you want to delete your name: ${deleteModal.entity.value}?`}
          isOpen={deleteModal.isOpen}
          title="Delete name?"
          onClose={handleDeleteModalClose}
          onFinish={handleDelete}
        />
      )}
    </>
  )
}

export default Names
